/* ---------------------------------------------------------------------------------------------
   KOSIMA -- live count on the filter drawer's "Apply N filters" button.

   The drawer is rendered with update_on_change: false, so ticking a box changes nothing on the
   page until the form is submitted. That means the count Liquid prints into the button is the
   count ALREADY APPLIED -- tick a fourth colour and the button still reads "Apply 3 filters",
   which reads as a bug. This keeps it in step with what is actually ticked.

   The labels come from the theme's locale file via data attributes on the footer, so translating
   the string in the theme editor translates this too. data-apply-label-other is rendered with
   count: 2 to force the plural form; the digit in it is a placeholder we swap for the real count.
   (Locales whose plural rules differ at 2 would need the digit substituted differently -- English,
   which is what this store ships, does not.)

   Listeners are on document rather than the form, so this survives the facets form being replaced
   by a section re-render.
   --------------------------------------------------------------------------------------------- */
(() => {
  const DRAWER_SELECTOR = 'facets-drawer';

  const countSelected = (drawer) => {
    // Every filter control the drawer renders is a checkbox or radio named "filter.*": the text
    // list, the size and colour boxes, and the in-stock switch. Price is the one exception -- it
    // is a pair of number inputs -- and counts as one filter when either end has been set, which
    // is the same rule sections/main-collection.liquid uses server-side.
    let count = drawer.querySelectorAll('input[name^="filter."]:checked').length;

    const priceInputs = drawer.querySelectorAll(
      'input[name="filter.v.price.gte"], input[name="filter.v.price.lte"]'
    );

    if (priceInputs.length > 0 && Array.from(priceInputs).some((input) => input.value !== '')) {
      count += 1;
    }

    return count;
  };

  const labelFor = (actions, count) => {
    if (count === 0) return actions.dataset.applyLabelZero || '';
    if (count === 1) return actions.dataset.applyLabelOne || '';
    return (actions.dataset.applyLabelOther || '').replace(/\d+/, String(count));
  };

  const refresh = (drawer) => {
    const actions = drawer.querySelector('.facets-drawer__actions');
    if (!actions) return;

    const applyButton = actions.querySelector('dialog-close-button .button');
    if (!applyButton) return;

    const label = labelFor(actions, countSelected(drawer));
    if (label && applyButton.textContent.trim() !== label) {
      applyButton.textContent = label;
    }
  };

  const refreshFromEvent = (event) => {
    const drawer = event.target.closest?.(DRAWER_SELECTOR);
    if (drawer) refresh(drawer);
  };

  document.addEventListener('change', refreshFromEvent);
  document.addEventListener('input', refreshFromEvent);

  // "Clear all" in the drawer footer (sections/main-collection.liquid, data-facets-clear). Capture phase on document,
  // so this runs BEFORE dialog-close-button's own click handler closes the drawer: untick every filter, empty the
  // price range and fire one change on the form (which marks the theme's facets form dirty). The drawer then closes
  // and the theme submits the emptied form exactly as it does for Apply. preventDefault stops the no-JS fallback
  // link from also navigating.
  document.addEventListener('click', (event) => {
    const clear = event.target.closest?.('[data-facets-clear]');
    if (!clear) return;

    const drawer = clear.closest(DRAWER_SELECTOR);
    const form = drawer?.querySelector('facets-form form');
    if (!form) return;

    event.preventDefault();

    form.querySelectorAll('input[name^="filter."]').forEach((input) => {
      if (input.type === 'checkbox' || input.type === 'radio') {
        input.checked = false;
      } else {
        input.value = '';
      }
    });

    form.dispatchEvent(new Event('change', { bubbles: true }));
    refresh(drawer);
  }, true);

  // Also correct the label the moment the drawer opens, in case the page was restored from
  // bfcache with boxes ticked that were never applied.
  document.addEventListener('dialog:after-show', (event) => {
    if (event.target.matches?.(DRAWER_SELECTOR)) refresh(event.target);
  });
})();
